import React, { useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTutorialStore } from '../../../application/store/TutorialStore';
import ConnectionActiveImports from '@features/connections/generic/components/ConnectionActiveImports';
import { APP_CONFIG } from '../../../config/app.config';
import OnboardingStepCard from '../shared/OnboardingStepCard';
import { useConnectionImportJobs } from '@features/connections/hooks/useConnectionImportJobs';

interface ImportProductsStepProps {
  onContinue: () => void;
}

const ImportProductsStep: React.FC<ImportProductsStepProps> = ({ onContinue }) => {
  const navigate = useNavigate();
  const colors = APP_CONFIG.ui.theme.colors;
  const { activeJobs, isFetched } = useConnectionImportJobs();
  const { markStepCompleted, completeUnlockDashboard } = useTutorialStore();

  useEffect(() => {
    // Wait for the active-jobs poll before skipping — otherwise a revisit via
    // the tutorial card races past running imports while the query is loading.
    if (!isFetched) return;
    if (activeJobs.length === 0) {
      onContinue();
    }
  }, [activeJobs.length, isFetched, onContinue]);

  const handleStartSetup = () => {
    markStepCompleted('marketplace-connection');
    completeUnlockDashboard();
    navigate('/dashboard');
  };

  return (
    <OnboardingStepCard
      title="Importing your data"
      subtitle="Depending on the size of your shop, the import can take several hours. You'll receive an email once it's finished."
      isActive
      footer={
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have to wait here. The import will continue in the background.
          </Typography>
          <Button sx={{ whiteSpace: 'nowrap' }} variant="outlined" onClick={handleStartSetup}>
            Start setting up
          </Button>
        </Stack>
      }
    >

      <Stack sx={{ border: `1px solid ${colors.border}`, borderRadius: '8px', overflow: 'auto' }}>
        <ConnectionActiveImports onImportsFinished={onContinue} />
      </Stack>


    </OnboardingStepCard>
  );
};

export default ImportProductsStep;
